import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { sprintService } from '@/services/sprintService'

const pairKey = (from, to) => `${from}-${to}`

// Optional workflow rules: which status may move to which. Off = any move is allowed (default).
// Admins and the project lead can change them; everyone else sees them read-only.
const WorkflowRules = ({ projectId, statuses, canEdit }) => {
  const queryClient = useQueryClient()
  const key = ['projects', String(projectId), 'workflow']
  const { data } = useQuery(key, () => sprintService.getWorkflow(projectId))
  const [restricted, setRestricted] = useState(false)
  const [allowed, setAllowed] = useState(() => new Set())
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!data?.data) return
    setRestricted(data.data.restricted)
    setAllowed(new Set(data.data.transitions.map(([from, to]) => pairKey(from, to))))
    setDirty(false)
  }, [data])

  const allPairs = () => new Set(statuses.flatMap(a => statuses.filter(b => b.id !== a.id).map(b => pairKey(a.id, b.id))))

  const toggleRestricted = (value) => {
    setRestricted(value)
    // Turning rules on starts from "everything allowed", so nothing breaks until boxes are cleared
    if (value && allowed.size === 0) setAllowed(allPairs())
    setDirty(true)
  }
  const toggle = (from, to) => {
    setAllowed(prev => {
      const next = new Set(prev)
      const k = pairKey(from, to)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
    setDirty(true)
  }

  const save = async () => {
    setSaving(true)
    try {
      const transitions = [...allowed].map(k => k.split('-').map(Number))
      await sprintService.setWorkflow(projectId, restricted, transitions)
      toast.success(restricted ? 'Workflow rules saved' : 'Workflow rules turned off')
      queryClient.invalidateQueries(key)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the workflow')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <label className="flex items-start justify-between gap-4 rounded-md border p-3">
        <span>
          <span className="block text-sm font-medium">Restrict status changes</span>
          <span className="block text-xs text-muted-foreground">
            Off: a task can move from any status to any other. On: only the moves ticked below are allowed — on the board,
            in bulk edit and for AI agents alike.{!canEdit && ' Only an admin or the project lead can change this.'}
          </span>
        </span>
        <Switch checked={restricted} onCheckedChange={toggleRestricted} disabled={!canEdit} aria-label="Restrict status changes" />
      </label>

      {restricted && statuses.length > 1 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">From ↓ / To →</th>
                {statuses.map(s => <th key={s.id} className="px-3 py-2 text-center text-xs font-medium">{s.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {statuses.map(from => (
                <tr key={from.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{from.name}</td>
                  {statuses.map(to => (
                    <td key={to.id} className="px-3 py-2 text-center">
                      {from.id === to.id ? <span className="text-muted-foreground">—</span> : (
                        <Checkbox
                          checked={allowed.has(pairKey(from.id, to.id))}
                          onCheckedChange={() => toggle(from.id, to.id)}
                          disabled={!canEdit}
                          aria-label={`Allow ${from.name} to ${to.name}`}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && dirty && (
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving}>Save workflow</Button>
          {restricted && <Button size="sm" variant="outline" onClick={() => { setAllowed(allPairs()); setDirty(true) }}>Allow everything</Button>}
        </div>
      )}
    </div>
  )
}

export default WorkflowRules
